import { useState } from "react";
import {
  Megaphone,
  HelpCircle,
  Scale,
  CalendarPlus,
} from "lucide-react";
import { Button, Dialog, Input, toast } from "../ui";
import { governanceApi, errMessage } from "./api";
import "./NewIssueDialog.css";

const KIND_OPTIONS = [
  {
    value: "TOPIC",
    label: "Topic for GBM",
    Icon: Megaphone,
    help:
      "Propose a topic for the General Body. At 10% member support the " +
      "board must convene a GBM within 30 days.",
  },
  {
    value: "QUERY",
    label: "Question to the board",
    Icon: HelpCircle,
    help:
      "A parliament-style question. The board must answer it in writing " +
      "or table it on the agenda of the next meeting.",
  },
  {
    value: "DISPUTE",
    label: "Dispute to Elders' Council",
    Icon: Scale,
    help:
      "Escalate a dispute. Every Elders' Council member is notified " +
      "immediately and a mediation meeting will be scheduled.",
  },
  {
    value: "MEETING_REQUEST",
    label: "Owners' meeting request",
    Icon: CalendarPlus,
    help:
      "Request a meeting of the owners. At 10% member support an owners' " +
      "meeting notice can be issued.",
  },
];

const TITLE_MIN = 8;
const BODY_MIN = 30;

export default function NewIssueDialog({ open, onOpenChange, onCreated }) {
  const [kind, setKind] = useState("TOPIC");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setKind("TOPIC");
    setTitle("");
    setBody("");
  };

  const close = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const titleShort = title.trim().length > 0 && title.trim().length < TITLE_MIN;
  const bodyShort = body.trim().length > 0 && body.trim().length < BODY_MIN;
  const valid =
    title.trim().length >= TITLE_MIN && body.trim().length >= BODY_MIN;

  const submit = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      const issue = await governanceApi.createIssue({
        kind,
        title: title.trim(),
        body: body.trim(),
      });
      toast.success("Issue raised — it is now open for the community.");
      reset();
      onOpenChange(false);
      if (onCreated) onCreated(issue);
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={close}
      title="Raise an issue"
      className="vsgov-newissue"
      footer={
        <>
          <Button variant="secondary" onClick={() => close(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={submitting}
            disabled={!valid}
          >
            Raise issue
          </Button>
        </>
      }
    >
      <div className="vsgov-kindgrid" role="radiogroup" aria-label="Issue kind">
        {KIND_OPTIONS.map(({ value, label, help, Icon }) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={kind === value}
            className={`vsgov-kindopt ${kind === value ? "active" : ""}`}
            onClick={() => setKind(value)}
          >
            <span className="vsgov-kindopt-head">
              <Icon size={15} />
              <span>{label}</span>
            </span>
            <span className="vsgov-kindopt-help">{help}</span>
          </button>
        ))}
      </div>

      <Input
        label="Title"
        placeholder="A short, clear headline"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        error={titleShort ? `At least ${TITLE_MIN} characters required` : undefined}
        helper={`Minimum ${TITLE_MIN} characters`}
        maxLength={200}
      />

      <div className="vsgov-field">
        <label className="vsgov-label" htmlFor="vsgov-newissue-body">
          Detailed rationale
        </label>
        <textarea
          id="vsgov-newissue-body"
          className="vsgov-textarea"
          rows={5}
          placeholder="Why does this need discussion? Give the full background so members can decide whether to support it."
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <p className={`vsgov-field-hint ${bodyShort ? "error" : ""}`}>
          {bodyShort
            ? `At least ${BODY_MIN} characters required (${body.trim().length}/${BODY_MIN})`
            : `Minimum ${BODY_MIN} characters — this is the case you are making to the community.`}
        </p>
      </div>
    </Dialog>
  );
}
